import type { Ref } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import type { LoaderProps } from '@/components/Loader';
import { Loader } from '@/components/Loader';
import type { NextImageProps } from '@/components/NextImage';
import { NextImageMemo } from '@/components/NextImage/NextImageMemo';
import type { ProductTypeOptions } from '@/lib/product';
type FullHeight = 'fullHeight';

export interface NextImageWithLoaderProps extends NextImageProps {
  disableLoader?: boolean;
  loaderHeight?: LoaderProps['height'];
  loaderNoMargin?: boolean;
  loaderSize?: LoaderProps['size'];
  loaderWidth?: LoaderProps['width'];
  productType?: ProductTypeOptions;
  verticalLoaderHeight?: number | FullHeight | '100vw';
}

export const NextImageWithLoader = forwardRef(
  (
    {
      className,
      disableLoader = false,
      loaderHeight,
      loaderNoMargin = false,
      loaderSize,
      loaderWidth,
      productType,
      showFallbackPlaceholder,
      showPlaceholderBackground,
      src,
      verticalLoaderHeight,
      ...props
    }: NextImageWithLoaderProps,
    ref: Ref<HTMLImageElement>,
  ) => {
    const [state, setState] = useState<'loading' | 'error' | 'loaded'>(
      'loading',
    );

    useEffect(() => {
      if (!src) {
        setState('error');
      }
    }, [src]);

    return (
      <>
        <NextImageWrapper
          className={
            disableLoader
              ? `${className || ''}`
              : `next-image_${state} ${className || ''}`
          }
        >
          {/* NOTE: Mb we could preload images on the client using Image and onload event. */}
          {state === 'loading' && !disableLoader && (
            <Loader
              height={loaderHeight}
              noMargin={loaderNoMargin}
              size={loaderSize}
              verticalLoaderHeight={verticalLoaderHeight}
              width={loaderWidth}
            />
          )}
          {state === 'error' ? (
            showFallbackPlaceholder && (
              <ImagePlaceholder
                className={`${state} ${className}`}
                productType={productType}
                sizes={props.sizes}
                withBackground={showPlaceholderBackground}
              />
            )
          ) : (
            <NextImageMemo
              ref={ref}
              className={`${state} ${className}`}
              setState={setState}
              src={src}
              {...props}
            />
          )}
        </NextImageWrapper>
      </>
    );
  },
);

interface NextImageWrapper {
  children: React.ReactNode;
  className?: string;
}

export const NextImageWrapper = ({ children, className }: NextImageWrapper) => (
  <div className={className}>{children}</div>
);

NextImageWithLoader.displayName = 'NextImage';
